import { useCallback } from 'react';

import { useUpdateOperation } from 'api/requests';
import { OperationEntity } from 'api/types/entity/operation';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';
import { OperationFormType } from 'modules/operation/common/forms/operationForm';
import { getOperationSubmitData } from 'modules/operation/common/helpers';

import { useLockAndUnlockOperation } from './useLockAndUnlockOperation';

export const useUpdateOperationAction = (operation: OperationEntity | null) => {
  const { lockOperationIfPossible, unlockOperation } =
    useLockAndUnlockOperation(operation);

  const {
    updateOperation,
    loading: submitLoading,
    error: submitError,
  } = useUpdateOperation();

  // GMT+5 (Алматы)
  // TODO: Feature request to get broker timezone offset from API
  const brokerTimezoneOffset = 18000;

  // Submit handler for the operation form
  const onSubmit: FormSubmit<OperationFormType> = useCallback(
    async (values, helpers) => {
      const submitData = getOperationSubmitData(values, brokerTimezoneOffset);
      const res = await updateOperation(
        operation?.id!,
        operation?.versionNumber!,
        submitData
      );

      if (res) {
        helpers.setReadOnly(true);
      }
    },
    [operation?.id, operation?.versionNumber, updateOperation]
  );

  // Enable edit mode
  const onEnableEdit = useCallback(
    async (onCallback: () => void) => {
      const isPossibleEdit = await lockOperationIfPossible?.();
      if (isPossibleEdit) {
        onCallback();
      }
    },
    [lockOperationIfPossible]
  );

  // Disable edit mode
  const onDisableEdit = useCallback(
    async (onCallback: () => void) => {
      unlockOperation?.();
      onCallback();
    },
    [unlockOperation]
  );

  useServerErrorNotify(submitError);

  return {
    submitLoading,
    onSubmit,
    onEnableEdit,
    onDisableEdit,
  };
};
